import React, { useEffect } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import { fade } from '@material-ui/core/styles/colorManipulator';
import { Close as CloseIcon, InfoOutlined as InfoIcon, Error as ErrorIcon } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { Button, IconButton } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: '500px',
    maxWidth: '600px',
    borderRadius: theme.spacing(1),
  },
  dialogTitle: {
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleText: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.error.main, // Reverted to error color
    fontWeight: 600,
  },
  errorIcon: {
    color: theme.palette.error.main,
    fontSize: '1.75rem',
  },
  closeButton: {
    padding: theme.spacing(1),
    marginRight: -theme.spacing(1),
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: fade(theme.palette.text.secondary, 0.1),
    },
  },
  content: {
    padding: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.primary,
    fontSize: '0.9rem',
  },
  infoBox: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(2.5),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  profileName: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: '1.1rem',
    marginBottom: theme.spacing(1.5),
  },
  fieldRow: {
    display: 'flex',
    marginBottom: theme.spacing(0.5),
  },
  fieldLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    minWidth: '100px',
    fontWeight: 500,
  },
  fieldValue: {
    color: theme.palette.text.primary,
    fontWeight: 500,
    fontSize: '0.875rem',
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  chip: {
    fontWeight: 500,
  },
  alert: {
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(1),
  },
  actions: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

interface DuplicateProfileDialogProps {
  open: boolean;
  onClose: () => void;
  profileName: string;
  tireSize?: string;
  axle?: string;
  duplicatedFields: string[];
}

export function DuplicateProfileDialog({
  open,
  onClose,
  profileName,
  tireSize,
  axle,
  duplicatedFields,
}: DuplicateProfileDialogProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      console.log('DuplicateProfileDialog opened with props:', { profileName, tireSize, axle, duplicatedFields });
    }
  }, [open, profileName, tireSize, axle, duplicatedFields]);

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.dialogPaper }} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box className={classes.titleContainer}>
          <Typography variant="h6" className={classes.titleText}>
            <ErrorIcon className={classes.errorIcon} />
            {t('Duplicate Profile Detected')}
          </Typography>
          <IconButton className={classes.closeButton} onClick={onClose} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Alert severity="warning" className={classes.alert}>
          {t('A tire profile with the same characteristics already exists in the system.')}
        </Alert>

        {/* Existing Profile Info */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            <InfoIcon fontSize="small" />
            {t('Existing Profile')}
          </Typography>
          <Box className={classes.infoBox}>
            <Typography className={classes.profileName}>{profileName}</Typography>

            {(tireSize || axle) && (
              <Grid container spacing={1}>
                {tireSize && (
                  <Grid item xs={12}>
                    <Box className={classes.fieldRow}>
                      <Typography className={classes.fieldLabel}>{t('Tire Size')}:</Typography>
                      <Typography className={classes.fieldValue}>{tireSize}</Typography>
                    </Box>
                  </Grid>
                )}
                {axle && (
                  <Grid item xs={12}>
                    <Box className={classes.fieldRow}>
                      <Typography className={classes.fieldLabel}>{t('Axle')}:</Typography>
                      <Typography className={classes.fieldValue}>{axle}</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            )}
          </Box>
        </Box>

        {/* Duplicated Fields */}
        {duplicatedFields.length > 0 && (
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              {t('Duplicated Fields')}
            </Typography>
            <Box className={classes.chipContainer}>
              {duplicatedFields.map((field) => (
                <Chip key={field} label={field} size="small" className={classes.chip} />
              ))}
            </Box>
          </Box>
        )}
        
        <Alert severity="warning" icon={false} className={classes.alert} style={{ marginBottom: 0 }}>
          {t('Please modify the profile information or use the existing profile.')}
        </Alert>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button onClick={onClose} color="primary" variant="contained">
          {t('OK')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
